import { Button } from "./ui/button";
import { ArrowLeft, Moon, Drama, RefreshCw } from "lucide-react";
import CamperPhoto from "./CamperPhoto";

/**
 * Renders Overnight + Broadway trip rosters, broken down by group.
 * Used by CIT counselor, Navigator supervisor, and Admin dashboards.
 *
 * Props:
 *   data           — response from GET /api/cit/overnight (scope-aware)
 *   loading        — boolean
 *   onBack         — handler for Dashboard back button
 *   headerSubtitle — small text under the page title
 */
export default function TripRostersView({ data, loading, onBack, headerSubtitle = "Trip rosters", onSync, syncing, onPrintExceptions, printingExceptions }) {
  const groups = data?.groups || [];
  const tripDates = data?.trip_dates || [];
  const overnightTotal = data?.overnight_total ?? 0;
  const broadwayTotal = data?.broadway_total ?? 0;
  const waitlistTotal = data?.waitlist_total ?? 0;

  const fmtDate = (s) => {
    try { return new Date(s + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }
    catch { return s; }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="px-2 gap-1" data-testid="trip-rosters-back-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Button>
            <div>
              <h1 className="text-sm sm:text-base font-bold text-slate-700">Overnight & Broadway</h1>
              <p className="text-xs text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onPrintExceptions && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-50" onClick={() => onPrintExceptions("Overnight")} disabled={printingExceptions} data-testid="trip-rosters-print-overnight-btn">
                  <Moon className="w-3 h-3 mr-1" />
                  {printingExceptions === "Overnight" ? "Generating…" : "Overnight List"}
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50" onClick={() => onPrintExceptions("Broadway")} disabled={printingExceptions} data-testid="trip-rosters-print-broadway-btn">
                  <Drama className="w-3 h-3 mr-1" />
                  {printingExceptions === "Broadway" ? "Generating…" : "Broadway List"}
                </Button>
              </>
            )}
            {onSync && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onSync} disabled={syncing} data-testid="trip-rosters-sync-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync from Sheet"}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-6">
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading...</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground" data-testid="trip-rosters-empty">
            No campers in scope are enrolled in either trip.
          </div>
        ) : (
          <>
            {/* Top-line summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="trip-rosters-totals">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-slate-700" />
                  <p className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Overnight</p>
                </div>
                <p className="text-2xl font-extrabold text-slate-800 mt-1">{overnightTotal}</p>
                {waitlistTotal > 0 && (
                  <p className="text-[11px] text-amber-700">{waitlistTotal} waitlisted</p>
                )}
              </div>
              <div className="p-3 bg-fuchsia-50 border border-fuchsia-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Drama className="w-4 h-4 text-fuchsia-700" />
                  <p className="text-[10px] uppercase font-bold text-fuchsia-700 tracking-wide">Broadway</p>
                </div>
                <p className="text-2xl font-extrabold text-fuchsia-800 mt-1">{broadwayTotal}</p>
              </div>
              {tripDates.length > 0 && (
                <div className="p-3 bg-white border border-slate-200 rounded-lg col-span-2 sm:col-span-1">
                  <p className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Overnight Dates</p>
                  <p className="text-xs font-semibold text-slate-700 mt-1">{tripDates.map(fmtDate).join(" · ")}</p>
                </div>
              )}
            </div>

            {/* One section per group */}
            {groups.map((g) => (
              <section
                key={g.group_code}
                className="border-2 border-slate-200 rounded-xl bg-white p-4"
                data-testid={`trip-rosters-group-${g.group_code}`}
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                  <div>
                    <h2 className="text-base font-extrabold text-slate-900">
                      <span className="inline-block bg-slate-100 text-slate-800 text-xs font-bold px-2 py-0.5 rounded mr-2">{g.group_code}</span>
                      {g.group_name && g.group_name !== g.group_code ? g.group_name : ""}
                    </h2>
                  </div>
                  <div className="flex gap-2 text-[11px]">
                    {g.overnight.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-700 font-semibold">
                        <Moon className="w-3 h-3" /> {g.overnight.length}
                      </span>
                    )}
                    {g.broadway.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 font-semibold">
                        <Drama className="w-3 h-3" /> {g.broadway.length}
                      </span>
                    )}
                  </div>
                </div>

                {/* Overnight block */}
                {g.overnight.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                      <Moon className="w-3 h-3" /> Overnight ({g.overnight.length})
                    </p>
                    <div className="space-y-1.5" data-testid={`trip-rosters-${g.group_code}-overnight`}>
                      {g.overnight.map((c) => (
                        <div key={`o-${c.id}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                          <div className="flex items-center gap-2.5">
                            <CamperPhoto camperId={c.id} firstName={c.first_name} lastName={c.last_name} size={32} />
                            <span className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                          </div>
                          {c.cit_subgroup && (
                            <span className="text-[10px] font-semibold text-slate-500">{c.cit_subgroup}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Waitlist block */}
                {g.waitlist.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-2">
                      Overnight Waitlist ({g.waitlist.length})
                    </p>
                    <div className="space-y-1.5" data-testid={`trip-rosters-${g.group_code}-waitlist`}>
                      {g.waitlist.map((c) => (
                        <div key={`w-${c.id}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50/50 border border-amber-200">
                          <span className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                          <span className="text-[10px] font-bold uppercase text-amber-700">Waitlist</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Broadway block */}
                {g.broadway.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-fuchsia-700 mb-2 flex items-center gap-1">
                      <Drama className="w-3 h-3" /> Broadway ({g.broadway.length})
                    </p>
                    <div className="space-y-1.5" data-testid={`trip-rosters-${g.group_code}-broadway`}>
                      {g.broadway.map((c) => (
                        <div key={`b-${c.id}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-fuchsia-50 border border-fuchsia-200">
                          <div className="flex items-center gap-2.5">
                            <CamperPhoto camperId={c.id} firstName={c.first_name} lastName={c.last_name} size={32} />
                            <span className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                          </div>
                          {c.cit_subgroup && (
                            <span className="text-[10px] font-semibold text-fuchsia-600">{c.cit_subgroup}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
